import { BasicButton, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useCaseMutation from "@/hooks/useCaseMutation";
import { today } from "@/libraries/date";
import { parseDate } from "@/libraries/dayjs-relative";
import { isAxiosError } from "@/libraries/helper";
import { UpdateCaseLetterFormFieldSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { createFileRoute, useLoaderData } from "@tanstack/react-router";
import omit from "lodash/omit";
import { useCallback } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/case/view/$id/letter/$letterId/update",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const onBack = useBackward();
	const letterId = Route.useParams({
		select: (s) => s.letterId,
	});

	const caseLetter = useLoaderData({
		from: "/dashboard/case/view/$id/letter/$letterId",
		// @ts-expect-error
		select: (s) => ({
			...s,
			date: s.date ? parseDate(s.date, "YYYY-MM-DD") : s.date,
			time: s.time ? parseDate(s.time, "HH:mm A") : s.time,
		}),
	});

	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<UpdateCaseLetterFormFieldSchema>({
		mode: "all",
		resolver: zodResolver(UpdateCaseLetterFormFieldSchema),
		defaultValues: {
			id: letterId,
			case_letter: caseLetter as UpdateCaseLetterFormFieldSchema["case_letter"],
		},
	});

	const { updateLetter } = useCaseMutation();

	const onSubmit = useCallback<SubmitHandler<UpdateCaseLetterFormFieldSchema>>(
		(data, e) => {
			e?.stopPropagation();
			e?.preventDefault();
			return updateLetter.mutateAsync(data, {
				onSuccess: () => {
					toast.success("Letter updated");
					return onBack();
				},
				onError: (err) => {
					if (isAxiosError(err)) {
						return toast.error((err.response?.data as any)?.messages);
					}
					toast.error("Failed, please try again");
				},
			});
		},
		[updateLetter, onBack],
	);

	return (
		<>
			<BackAppBar title="Update Discharge Letter" />
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="flex-1 flex flex-col overflow-hidden"
			>
				<div className="flex-1 bg-gray-light flex flex-col gap-2 overflow-y-auto">
					<div className="container bg-white flex flex-col gap-6 py-4">
						<Typography fontWeight={900} fontSize={14}>
							Date
						</Typography>
						<Controller
							control={control}
							name="case_letter.date"
							render={({ field }) => {
								return (
									<div className="w-full">
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<DatePicker
												label="Select Date"
												className="!w-full [&_.MuiInputBase-root]:!rounded-xl"
												format="DD MMMM YYYY"
												disableFuture={true}
												maxDate={today()}
												slots={{
													openPickerIcon: CalendarTodayIcon,
												}}
												{...omit(field, ["ref"])}
											/>
										</LocalizationProvider>
										<Typography
											fontWeight={400}
											fontSize={12}
											className="pl-4 text-[#00000099]"
										>
											Select the relevant date for this letter
										</Typography>
									</div>
								);
							}}
						/>
						<Controller
							control={control}
							name="case_letter.time"
							render={({ field }) => {
								return (
									<div className="w-full">
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<TimePicker
												label="Select Time"
												className="!w-full [&_.MuiInputBase-root]:!rounded-xl"
												{...omit(field, ["ref"])}
											/>
										</LocalizationProvider>
										<Typography
											fontWeight={400}
											fontSize={12}
											className="pl-4 text-[#00000099]"
										>
											Select the relevant date for this letter
										</Typography>
									</div>
								);
							}}
						/>
					</div>
					<div className="container bg-white flex flex-col gap-6 py-4">
						<Typography fontWeight={900} fontSize={14}>
							Letter Details
						</Typography>
						<Controller
							control={control}
							name="case_letter.diagnosis"
							render={({ field, fieldState }) => (
								<TextField
									label="Diagnosis"
									placeholder="Enter Diagnosis"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Primary diagnosis or condition treated during admission"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
						<Controller
							control={control}
							name="case_letter.medication"
							render={({ field, fieldState }) => (
								<TextField
									multiline
									rows={3}
									label="Medication"
									placeholder="Enter Medication"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Current medications with dosage and duration"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
						<Controller
							control={control}
							name="case_letter.allergies"
							render={({ field, fieldState }) => (
								<TextField
									multiline
									rows={3}
									label="Allergies"
									placeholder="Enter Allergies"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Document any known drug allergies or adverse reactions"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
						<Controller
							control={control}
							name="case_letter.summary"
							render={({ field, fieldState }) => (
								<TextField
									multiline
									rows={3}
									label="Case Summary / Message"
									placeholder="Enter Case Summary"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Main content and clinical information"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
						<Controller
							control={control}
							name="case_letter.plan"
							render={({ field, fieldState }) => (
								<TextField
									multiline
									rows={3}
									label="Plan"
									placeholder="Enter Plan"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Recommendations and next steps"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
					</div>
				</div>
				<div className="container bg-white py-4 shadow">
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						className="!h-12 text-sm font-bold !shadow-none"
						fullWidth
						disable={!isDirty || !isValid}
						loading={isSubmitting}
					>
						Submit
					</BasicButton>
				</div>
			</form>
		</>
	);
}
